import type { FC, ReactNode } from 'react'

import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { SilkDrift } from '@/components/dots/silk-drift'
import { SectionLabel } from '@/components/landing/section-label'
import { ServiceMotif } from '@/components/landing/service-motif'
import { SERVICES } from '@/constants/content'

export const ServicesSection: FC = (): ReactNode => {
    return (
        <section id='services' className='relative isolate border-t border-line bg-white'>
            <SilkDrift />

            <Container className='py-24 md:py-36'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='02'>Services</SectionLabel>

                        <RevealText
                            lines={["Everything it takes to", "design, ship and scale."]}
                            className='mt-8 text-[clamp(2.5rem,5.4vw,5rem)] font-medium leading-[0.95] tracking-[-0.045em] text-ink'
                        />
                    </div>

                    <Reveal className='lg:col-span-4 lg:col-start-9'>
                        <p className='text-lg leading-relaxed text-ink/70'>
                            One senior team from the first workshop to production and beyond. No hand-offs between agencies, no lost context, one shared roadmap.
                        </p>
                    </Reveal>
                </div>

                <ul className='mt-16 grid border-t border-l border-line sm:grid-cols-2 md:mt-24 xl:grid-cols-4'>
                    {SERVICES.map((service, index) => (
                        <li
                            key={service.number}
                            className='flex flex-col border-r border-b border-line bg-white transition-colors duration-500 hover:bg-azure-50'
                        >
                            <Reveal delay={index * 90} className='flex h-full flex-col'>
                                <div className='relative h-48 border-b border-line md:h-52'>
                                    <ServiceMotif motif={service.motif} />

                                    <span className='absolute top-5 left-5 font-mono text-[11px] tracking-[0.14em] text-muted-foreground'>
                                        {service.number}
                                    </span>
                                </div>

                                <div className='flex flex-1 flex-col p-6 md:p-7'>
                                    <h3 className='text-2xl font-medium tracking-[-0.03em] text-ink'>
                                        {service.title}
                                    </h3>

                                    <p className='mt-3 text-[15px] leading-relaxed text-muted-foreground'>
                                        {service.description}
                                    </p>

                                    <ul className='mt-auto flex flex-wrap gap-2 pt-8'>
                                        {service.capabilities.map(capability => (
                                            <li
                                                key={capability}
                                                className='rounded-full border border-line bg-white px-3 py-1 font-mono text-[11px] uppercase tracking-[0.08em] text-ink/70'
                                            >
                                                {capability}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        </li>
                    ))}
                </ul>
            </Container>
        </section>
    )
}
